import React, { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Divider, Grid, Link, Typography, PaperProps } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { Network, SocialAccount } from 'api';
import { Buttons, Capability, PaddedPaper, SocialIcon, UserAvatar } from 'components';
import { useCurrentUser, useNetworkCapabilities, useSocialConnections } from 'hooks';
import { connectionPopup, formatStat, getFullName, getSocialAccountName } from 'helpers';
import { Account } from 'Links';

interface Props {
  elevation: PaperProps['elevation'];
}

const useStyles = makeStyles(theme => ({
  divider: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
}));

const TotalReach: FC<Props> = ({ elevation }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  const { connections, twitterAccounts, linkedinAccounts } = useSocialConnections();
  const { hasTwitter, hasLinkedin } = useNetworkCapabilities();

  const { follower_count, reach, job_title } = currentUser;

  function renderValue(account: SocialAccount) {
    return (
      <Typography variant="subtitle1" color="textSecondary">
        {formatStat(account.reach)}
      </Typography>
    );
  }

  function renderRefresh(account: SocialAccount) {
    return <Buttons.Refresh account={account} />;
  }

  function renderItem(account: SocialAccount) {
    return (
      <Grid key={account.id} item container alignItems="center" spacing={1}>
        <Grid item>
          <SocialIcon provider={account.provider} />
        </Grid>
        <Grid item xs zeroMinWidth>
          <Typography noWrap variant="subtitle1">
            {getSocialAccountName(account)}
          </Typography>
        </Grid>
        <Grid item>{!account.expires_soon ? renderValue(account) : renderRefresh(account)}</Grid>
      </Grid>
    );
  }

  function renderConnect(network: Network) {
    const connectKey = network === Network.Twitter ? 'connectToTwitter' : 'connectToLinkedIn';

    return (
      <Grid item container alignItems="center" spacing={1}>
        <Grid item>
          <SocialIcon provider={network} />
        </Grid>
        <Grid item xs zeroMinWidth>
          <Link
            component="button"
            noWrap
            variant="subtitle1"
            onClick={() => connectionPopup(network)}
          >
            {t(`user:${connectKey}`)}
          </Link>
        </Grid>
      </Grid>
    );
  }

  return (
    <PaddedPaper elevation={elevation} padding="24px 16px">
      <Grid container direction="column" alignItems="center" wrap="nowrap" spacing={2}>
        <Grid item>
          <UserAvatar size={100} user={currentUser} />
        </Grid>
        <Grid item zeroMinWidth xs>
          <Typography noWrap align="center" variant="h6">
            {getFullName(currentUser)}
          </Typography>
          <Typography color="textSecondary" noWrap align="center" variant="subtitle1">
            {job_title}
          </Typography>
        </Grid>
      </Grid>
      <Divider className={classes.divider} />
      <Typography align="center" variant="subtitle2">
        {t('components:yourNetworkReach')}
      </Typography>
      <Typography paragraph align="center" variant="h3">
        {formatStat(follower_count + reach)}
      </Typography>
      <Grid container direction="column" spacing={1}>
        {hasTwitter && (
          <Fragment>
            {!twitterAccounts.length
              ? renderConnect(Network.Twitter)
              : twitterAccounts.map(renderItem)}
          </Fragment>
        )}
        {hasLinkedin && (
          <Fragment>
            {!linkedinAccounts.length
              ? renderConnect(Network.Linkedin)
              : linkedinAccounts.map(renderItem)}
          </Fragment>
        )}
      </Grid>
      {!!connections.length && (
        <Capability name="Sharing.Function.ConnectMultipleAccounts">
          <Box paddingTop={2}>
            <Button component={Account} fullWidth>
              {t('sharing:connectMore')}
            </Button>
          </Box>
        </Capability>
      )}
    </PaddedPaper>
  );
};

export default TotalReach;
